import type { Group, LoadingManager, Mesh, Object3D } from 'three'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader'
import { GLTFLoader } from '../extend/GLTFLoader'
import { getAllMeshChildren } from '../utils'
import type { InteractionManager } from '../core/event/global'
import provideEventPrototype from './events'

/**
 * inject event prototype fn
 * @param object3d
 */
const injectEventPrototype = (object3d: Object3D) => {
  const allMesh = getAllMeshChildren(object3d)
  allMesh.forEach((mesh) => {
    Object.assign(mesh, provideEventPrototype(mesh.raycast.bind(mesh), mesh as Mesh))
  })
}

const loadTask = new Map<string, {
  isLoaded: boolean
}>()

const emitLoadEvent = () => {}

/**
 * @file 待废除
 */

/**
 * @deprecated 即将废除， 请使用 LoaderModule
 */
class ModelLoader {
  interactionManager: InteractionManager | null = null

  private loadingManager: LoadingManager | undefined

  // loadTask: Map<string>

  constructor(loadingManager?: LoadingManager) {
    this.loadingManager = loadingManager
    // this.loadTask = new Map<string>()
  }

  private initInteractionManager() {
    console.warn('ModelLoader.initInteractionManager is deprecated and no longer functional.')
  }

  private addEvent(object3d: Object3D) {
    if (!this.interactionManager)
      this.initInteractionManager()

    this.interactionManager!.add(object3d)
  }

  /**
   * gltf model loader
   * @param url
   * @param openEvents
   * @param draco
   * @param decoderPath
   * @param onLoad
   * @param onProgress
   * @param onError
   * @returns
   */
  public loadGLTF(url: string,
    openEvents = false,
    draco = false,
    decoderPath = './draco/',
    onLoad?: (result: GLTF) => GLTF,
    onProgress?: (event: ProgressEvent) => void,
    onError?: (event: ErrorEvent) => void) {
    const loader = new GLTFLoader(this.loadingManager)

    if (draco) {
      // default draco file path is './draco/', now not support custom
      const dracoLoader = new DRACOLoader(this.loadingManager)

      dracoLoader.setDecoderPath(decoderPath)
      dracoLoader.setDecoderConfig({ type: 'js' })
      dracoLoader.preload()

      loader.setDRACOLoader(dracoLoader)
    }

    loadTask.set(url, {
      isLoaded: false,
    })

    return new Promise((resolve, reject) => {
      loader.load(url,
        (gltf: any) => {
          onLoad ? resolve(onLoad(gltf)) : resolve(gltf)

          setTimeout(() => {
            loadTask.set(url, {
              isLoaded: true,
            })

            emitLoadEvent()
          }, 100)
        },
        (xhr: any) => {
          onProgress && onProgress(xhr)
        },
        (err: any) => {
          onError && onError(err)
          reject(err)
        })
    }) as Promise<GLTF>
  }

  /**
   * fbx model loader
   * @param url
   * @param openEvents 是否打开事件传播
   * @param onLoad
   * @param onProgress
   * @param onError
   */
  public loadFbx(url: string,
    openEvents = false,
    onLoad?: (result: Group) => Group,
    onProgress?: (event: ProgressEvent) => void,
    onError?: (event: ErrorEvent) => void) {
    const fbxLoader = new FBXLoader(this.loadingManager)

    loadTask.set(url, {
      isLoaded: false,
    })

    return new Promise((resolve, reject) => {
      fbxLoader.load(url,
        (fbx) => {
          if (openEvents)
            this.addEvent(fbx)

          onLoad ? resolve(onLoad(fbx)) : resolve(fbx)

          setTimeout(() => {
            loadTask.set(url, {
              isLoaded: true,
            })

            emitLoadEvent()
          }, 100)
        },
        (xhr) => {
          onProgress && onProgress(xhr)
        },
        (err: any) => {
          onError && onError(err)
          reject(err)
        })
    }) as Promise<Group>
  }

  /**
   * parse fbx buffer
   * @param buffer
   * @param path
   * @returns
   */
  public parseFbxBuffer(buffer: ArrayBuffer | string, path: string) {
    return new FBXLoader().parse(buffer, path)
  }
}

export default ModelLoader